import { FC, useState } from 'react';
import { message, Tabs } from 'antd';
import {
    DollarOutlined,
    SlidersOutlined,
    SafetyCertificateOutlined,
    SwapOutlined,
    ThunderboltOutlined,
    WalletOutlined,
} from '@ant-design/icons';
import { Card } from '../../../../../components/card/card';
import { API } from '../../../../../apis';
import { postViaAuth, responseValidator } from '../../../../../apis/api';
import {
    CommissionSettings,
    CryptoWithdrawalFeeSettings,
    DangerousAmountsSettings,
    SucceedBuyOrderLimitConditionsSettings,
    TradeAmountLimitSettings,
    WithdrawKindSettings,
} from '../../../../../core/domain';
import { NumberInput, SettingsSection, SwitchField } from '../shared/settings-ui';
import { useLoadSettings } from '../shared/use-settings';
import '../settings.scss';

const toNum = (v: string | number | undefined) => Number(v ?? 0) || 0;

/* -------------------------------- کارمزد -------------------------------- */
const CommissionTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<CommissionSettings>(API.siteSettings.commissionSetting, setCardLoading);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        const res: any = await postViaAuth(API.siteSettings.editCommissionSetting, { amount: toNum(data?.commission) });
        setSaving(false);
        if (responseValidator(res.status)) message.success('کارمزد با موفقیت ذخیره شد.');
    };

    return (
        <SettingsSection
            title="کارمزد معاملات"
            description="درصد کارمزدی که روی معاملات کاربران اعمال می‌شود."
            onSave={save}
            loading={saving}
        >
            <NumberInput
                label="کارمزد (درصد)"
                value={data?.commission}
                allowDecimal
                onChange={(v) => setData({ ...data, commission: v as any })}
            />
        </SettingsSection>
    );
};

/* -------------------- محدودیت مبلغ معامله -------------------- */
const TradeAmountLimitTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<TradeAmountLimitSettings>(
        API.siteSettings.tradeAmountLimitSettings,
        setCardLoading,
    );
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        const res: any = await postViaAuth(API.siteSettings.editTradeAmountLimitSettings, {
            cryptMinTradeAmount: toNum(data?.cryptoMinTradeAmount),
            perfectMoneyMinTradeAmount: toNum(data?.utopiaMinTradeAmount),
            irtMinTradeAmount: toNum(data?.irtMinTradeAmount),
        });
        setSaving(false);
        if (responseValidator(res.status)) message.success('محدودیت‌های معامله ذخیره شد.');
    };

    return (
        <SettingsSection
            title="حداقل مبلغ معامله"
            description="حداقل مقدار مجاز برای هر نوع معامله."
            onSave={save}
            loading={saving}
        >
            <NumberInput
                label="حداقل معامله کریپتو (USDT)"
                value={data?.cryptoMinTradeAmount}
                allowDecimal
                onChange={(v) => setData({ ...data, cryptoMinTradeAmount: v as any })}
            />
            <NumberInput
                label="حداقل معامله یوتوپیا"
                value={data?.utopiaMinTradeAmount}
                allowDecimal
                onChange={(v) => setData({ ...data, utopiaMinTradeAmount: v as any })}
            />
            <NumberInput
                label="حداقل معامله ریالی (تومان)"
                value={data?.irtMinTradeAmount}
                separated
                onChange={(v) => setData({ ...data, irtMinTradeAmount: v as any })}
            />
        </SettingsSection>
    );
};

/* ---------------- محدودیت سفارش خرید ---------------- */
const SucceedBuyOrderLimitTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<SucceedBuyOrderLimitConditionsSettings>(
        API.siteSettings.succeedBuyOrderLimitConditionsSettings,
        setCardLoading,
    );
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        const res: any = await postViaAuth(API.siteSettings.editSucceedBuyOrderLimitConditionsSettings, {
            succeedBuyOrderLimitConditionsSettings: {
                withoutLimits: toNum(data?.withoutLimits),
                maxAmountForDocument: toNum(data?.maxAmountForDocument),
            },
        });
        setSaving(false);
        if (responseValidator(res.status)) message.success('محدودیت‌های سفارش خرید ذخیره شد.');
    };

    return (
        <SettingsSection
            title="محدودیت‌های سفارش خرید"
            description="سقف‌های مبلغی برای پردازش خودکار سفارش‌های خرید."
            onSave={save}
            loading={saving}
        >
            <NumberInput
                label="سقف بدون محدودیت (تومان)"
                value={data?.withoutLimits}
                separated
                onChange={(v) => setData({ ...data, withoutLimits: v as any })}
            />
            <NumberInput
                label="حداکثر مبلغ برای مدرک (تومان)"
                value={data?.maxAmountForDocument}
                separated
                onChange={(v) => setData({ ...data, maxAmountForDocument: v as any })}
            />
        </SettingsSection>
    );
};

/* -------------------- مقادیر حساس -------------------- */
const DangerousAmountTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<DangerousAmountsSettings>(
        API.siteSettings.dangerousAmountsSetting,
        setCardLoading,
    );
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        const res: any = await postViaAuth(API.siteSettings.changeDangerousAmountSettings, {
            forLevel1: toNum(data?.forLevel1),
            forLevel2: toNum(data?.forLevel2),
        });
        setSaving(false);
        if (responseValidator(res.status)) message.success('مقادیر حساس ذخیره شد.');
    };

    return (
        <SettingsSection
            title="مقادیر حساس"
            description="آستانه مبلغی که تراکنش‌های بالاتر از آن نیازمند بررسی بیشتر هستند."
            onSave={save}
            loading={saving}
        >
            <NumberInput
                label="آستانه سطح ۱ (تومان)"
                value={data?.forLevel1}
                separated
                onChange={(v) => setData({ ...data, forLevel1: v as any })}
            />
            <NumberInput
                label="آستانه سطح ۲ (تومان)"
                value={data?.forLevel2}
                separated
                onChange={(v) => setData({ ...data, forLevel2: v as any })}
            />
        </SettingsSection>
    );
};

/* -------------------- برداشت خودکار -------------------- */
const WithdrawKindTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<WithdrawKindSettings>(
        API.siteSettings.withdrawaSettings,
        setCardLoading,
    );
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        const res: any = await postViaAuth(API.siteSettings.changeWithdrawaSettings, {
            isAutoCryptoWidthraw: !!data?.isAutoCryptoWidthraw,
            isAutoCurrencyWidthraw: !!data?.isAutoCurrencyWidthraw,
            isAutoUtopiaWidthraw: !!data?.isAutoUtopiaWidthraw,
        });
        setSaving(false);
        if (responseValidator(res.status)) message.success('تنظیمات برداشت خودکار ذخیره شد.');
    };

    return (
        <SettingsSection
            title="برداشت خودکار"
            description="مشخص می‌کند کدام نوع برداشت‌ها بدون تایید دستی ادمین پردازش شوند."
            onSave={save}
            loading={saving}
        >
            <div className="settings-col-span-2">
                <SwitchField
                    label="برداشت خودکار کریپتو"
                    checked={data?.isAutoCryptoWidthraw}
                    onChange={(c) => setData({ ...data, isAutoCryptoWidthraw: c } as WithdrawKindSettings)}
                />
            </div>
            <div className="settings-col-span-2">
                <SwitchField
                    label="برداشت خودکار ریالی"
                    checked={data?.isAutoCurrencyWidthraw}
                    onChange={(c) => setData({ ...data, isAutoCurrencyWidthraw: c } as WithdrawKindSettings)}
                />
            </div>
            <div className="settings-col-span-2">
                <SwitchField
                    label="برداشت خودکار یوتوپیا"
                    checked={data?.isAutoUtopiaWidthraw}
                    onChange={(c) => setData({ ...data, isAutoUtopiaWidthraw: c } as WithdrawKindSettings)}
                />
            </div>
        </SettingsSection>
    );
};

/* -------------- کارمزد برداشت کریپتو -------------- */
const CryptoWithdrawalFeeTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<CryptoWithdrawalFeeSettings>(
        API.siteSettings.cryptoWithdrawalFeeSettings,
        setCardLoading,
    );
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        const res: any = await postViaAuth(API.siteSettings.changeCryptoWithdrawalFeeSetting, {
            usdt: toNum(data?.usdt),
            trx: toNum(data?.trx),
            bep20StableCoin: toNum(data?.bep20StableCoin),
        });
        setSaving(false);
        if (responseValidator(res.status)) message.success('کارمزد برداشت کریپتو ذخیره شد.');
    };

    return (
        <SettingsSection
            title="کارمزد برداشت کریپتو"
            description="کارمزد ثابت شبکه که هنگام برداشت از کاربر کسر می‌شود."
            onSave={save}
            loading={saving}
        >
            <NumberInput
                label="کارمزد USDT"
                value={data?.usdt}
                allowDecimal
                onChange={(v) => setData({ ...data, usdt: v as any })}
            />
            <NumberInput
                label="کارمزد TRX"
                value={data?.trx}
                allowDecimal
                onChange={(v) => setData({ ...data, trx: v as any })}
            />
            <NumberInput
                label="کارمزد USDT روی شبکه BEP20"
                value={data?.bep20StableCoin}
                allowDecimal
                onChange={(v) => setData({ ...data, bep20StableCoin: v as any })}
            />
        </SettingsSection>
    );
};

export const FinancialSettings: FC = () => {
    const [cardLoading, setCardLoading] = useState(false);

    const items = [
        { key: 'commission', label: 'کارمزد', icon: <DollarOutlined />, children: <CommissionTab setCardLoading={setCardLoading} /> },
        { key: 'trade-limit', label: 'محدودیت معامله', icon: <SlidersOutlined />, children: <TradeAmountLimitTab setCardLoading={setCardLoading} /> },
        { key: 'buy-order-limit', label: 'محدودیت سفارش خرید', icon: <SafetyCertificateOutlined />, children: <SucceedBuyOrderLimitTab setCardLoading={setCardLoading} /> },
        { key: 'dangerous-amount', label: 'مقادیر حساس', icon: <ThunderboltOutlined />, children: <DangerousAmountTab setCardLoading={setCardLoading} /> },
        { key: 'withdraw-kind', label: 'برداشت خودکار', icon: <SwapOutlined />, children: <WithdrawKindTab setCardLoading={setCardLoading} /> },
        { key: 'crypto-fee', label: 'کارمزد برداشت کریپتو', icon: <WalletOutlined />, children: <CryptoWithdrawalFeeTab setCardLoading={setCardLoading} /> },
    ];

    return (
        <Card blur={cardLoading} className="pmx-settings-page">
            <Tabs items={items} destroyInactiveTabPane />
        </Card>
    );
};
